import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Search, Filter, ExternalLink, Calendar, Users, FileText, Loader2, Star, Clock, ChevronDown, ChevronUp, Mic } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/PageHeader';
import { VoiceInput } from '../components/VoiceInput';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export const BiblioPage = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedAbstracts, setExpandedAbstracts] = useState({});
  const [filters, setFilters] = useState({
    reviews_only: false,
    clinical_trials_only: false,
    meta_analysis_only: false,
    free_full_text: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/biblio/history?limit=10`);
      setSearchHistory(response.data.searches || []);
    } catch (error) {
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) {
      toast.error('Veuillez entrer une requête de recherche');
      return;
    }

    setLoading(true);
    try {
      // Build active filters
      const activeFilters = {};
      if (filters.reviews_only) activeFilters.reviews_only = true;
      if (filters.clinical_trials_only) activeFilters.clinical_trials_only = true;
      if (filters.meta_analysis_only) activeFilters.meta_analysis_only = true;
      if (filters.free_full_text) activeFilters.free_full_text = true;

      const response = await axios.post(`${API}/biblio/search`, {
        query: query,
        max_results: 25,
        filters: Object.keys(activeFilters).length > 0 ? activeFilters : null
      });

      setResults(response.data);
      fetchHistory();
      toast.success(`${response.data.total_results} articles trouvés`);
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const toggleAbstract = (pmid) => {
    setExpandedAbstracts(prev => ({
      ...prev,
      [pmid]: !prev[pmid]
    }));
  };

  const handleHistoryClick = (historyQuery) => {
    setQuery(historyQuery);
  };

  const handleVoiceTranscription = (text) => {
    setQuery(text);
    setTimeout(() => handleSearch(), 300);
  };

  const getArticleTypeBadge = (types) => {
    if (types.includes('Meta-Analysis')) return { label: 'Méta-analyse', color: 'bg-purple-600' };
    if (types.includes('Systematic Review')) return { label: 'Revue systématique', color: 'bg-indigo-600' };
    if (types.includes('Randomized Controlled Trial')) return { label: 'ECR', color: 'bg-green-600' };
    if (types.includes('Clinical Trial')) return { label: 'Essai clinique', color: 'bg-emerald-600' };
    if (types.includes('Review')) return { label: 'Revue', color: 'bg-blue-600' };
    if (types.includes('Case Reports')) return { label: 'Cas clinique', color: 'bg-amber-600' };
    if (types.includes('Guideline')) return { label: 'Recommandation', color: 'bg-red-600' };
    return null;
  };

  return (
    <div data-testid="biblio-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <PageHeader icon={BookOpen} title="Bibliographie Médicale" subtitle="Recherche dans PubMed et bases de données de haute qualité" gradient="from-violet-600 to-purple-600" testId="biblio-header" />

      {/* Search Form */}
      <div className="glassmorphic rounded-2xl p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                data-testid="biblio-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ex: melanoma immunotherapy, hypertension treatment guidelines..."
                className="w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
              />
            </div>
          </div>
          
          {/* Voice Input & Search Button */}
          <div className="flex gap-3">
            <VoiceInput 
              onTranscription={handleVoiceTranscription}
              placeholder="Parler"
              disabled={loading}
              className="flex-shrink-0"
            />
            <Button
              data-testid="biblio-search-button"
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-xl"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Rechercher
                </>
              )}
            </Button>
          </div>

          {/* Filters Toggle */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filtres avancés
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Filters */}
          {showFilters && (
            <div className="flex flex-wrap gap-3 p-4 bg-slate-900/40 rounded-xl">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.meta_analysis_only}
                  onChange={(e) => setFilters(prev => ({ ...prev, meta_analysis_only: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-300">Méta-analyses</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.reviews_only}
                  onChange={(e) => setFilters(prev => ({ ...prev, reviews_only: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-300">Revues</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.clinical_trials_only}
                  onChange={(e) => setFilters(prev => ({ ...prev, clinical_trials_only: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-300">Essais cliniques</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.free_full_text}
                  onChange={(e) => setFilters(prev => ({ ...prev, free_full_text: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-300">Accès libre uniquement</span>
              </label>
            </div>
          )}

          {/* Search History */}
          {searchHistory.length > 0 && !results && (
            <div className="pt-4 border-t border-slate-800">
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Recherches récentes
              </p>
              <div className="flex flex-wrap gap-2">
                {searchHistory.slice(0, 5).map((search, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleHistoryClick(search.query)}
                    className="px-3 py-1 text-xs bg-slate-800 text-slate-300 rounded-full hover:bg-slate-700 transition-colors"
                  >
                    {search.query.length > 30 ? search.query.slice(0, 30) + '...' : search.query}
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Results Header */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">Résultats</h2>
              <span className="px-3 py-1 bg-violet-600/30 text-violet-300 rounded-full text-sm">
                {results.total_results} articles
              </span>
            </div>
            <div className="text-sm text-slate-400">
              Source: PubMed / NCBI
            </div>
          </div>

          {/* Articles List */}
          {results.databases?.map(db => (
            <div key={db.name} className="space-y-3">
              {db.articles?.map((article) => {
                const typeBadge = getArticleTypeBadge(article.article_types || []);
                const isExpanded = expandedAbstracts[article.pmid];

                return (
                  <div
                    key={article.pmid}
                    className="glassmorphic rounded-xl p-3.5 sm:p-5 hover:border-violet-500/50 transition-all"
                  >
                    {/* Article Header */}
                    <div className="flex items-start justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Type Badge */}
                        <div className="flex items-center gap-2 mb-2">
                          {typeBadge && (
                            <span className={`px-2 py-0.5 ${typeBadge.color} text-white text-xs rounded-full`}>
                              {typeBadge.label}
                            </span>
                          )}
                          <span className="text-xs text-slate-500">PMID: {article.pmid}</span>
                        </div>

                        {/* Title */}
                        <h3 className="text-white font-medium text-[15px] sm:text-base leading-snug mb-2 break-words">
                          {article.title}
                        </h3>

                        {/* Authors & Journal */}
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {article.authors_display}
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {article.journal_abbrev}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {article.pub_date}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Ouvrir sur PubMed"
                        className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-violet-600/20 text-violet-400 rounded-lg hover:bg-violet-600/30 active:scale-95 transition-all"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>

                    {/* Abstract */}
                    <div className="mt-3">
                      <button
                        onClick={() => toggleAbstract(article.pmid)}
                        className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1 min-h-[44px] -my-2"
                      >
                        {isExpanded ? 'Masquer' : 'Voir'} l'abstract
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>

                      {isExpanded && (
                        <div className="mt-3 p-4 bg-slate-900/60 rounded-lg">
                          <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
                            {article.abstract}
                          </p>
                          
                          {/* Keywords */}
                          {article.keywords?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-700">
                              <p className="text-xs text-slate-500 mb-2">Mots-clés MeSH:</p>
                              <div className="flex flex-wrap gap-1">
                                {article.keywords.slice(0, 8).map((kw, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-slate-800 text-slate-400 text-xs rounded">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* DOI Link */}
                          {article.doi && (
                            <div className="mt-3 pt-3 border-t border-slate-700">
                              <a
                                href={`https://doi.org/${article.doi}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-violet-400 hover:underline"
                              >
                                DOI: {article.doi}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* No Results */}
          {results.total_results === 0 && (
            <div className="glassmorphic rounded-xl p-8 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400">Aucun article trouvé pour cette recherche</p>
              <p className="text-sm text-slate-500 mt-2">
                Essayez de modifier vos termes de recherche ou d'élargir vos filtres
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!results && !loading && (
        <div className="glassmorphic rounded-xl p-8 text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-700" />
          <p className="text-slate-400 mb-2">Recherchez des articles médicaux</p>
          <p className="text-sm text-slate-500">
            Accès direct à PubMed avec filtres avancés (méta-analyses, essais cliniques, etc.)
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <span className="px-3 py-1 bg-slate-800 text-slate-400 rounded-full text-sm">PubMed</span>
            <span className="px-3 py-1 bg-slate-800 text-slate-400 rounded-full text-sm">MEDLINE</span>
            <span className="px-3 py-1 bg-slate-800 text-slate-400 rounded-full text-sm">NCBI</span>
          </div>
        </div>
      )}
    </div>
  );
};
